import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { MaskedLine, Reveal } from "../components/Reveal";
import { ContourLines } from "../components/ContourLines";
import { Marquee } from "../components/Marquee";
import { NeedSelector } from "../components/NeedSelector";
import { ServiceCard, ProblemCard, GuideCard } from "../components/Cards";
import { ProcessSteps } from "../components/ProcessSteps";
import { TrustSection } from "../components/TrustSection";
import { FAQAccordion, faqSchema } from "../components/FAQAccordion";
import { CTASection } from "../components/CTASection";
import { business } from "../content/site";
import { trackEvent } from "../lib/analytics";
import { useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices, useGuides, useHomeFaqs } from "../i18n/content";

const HERO_IMG = "/hero-campo.webp";

export default function Home() {
  const services = useServices();
  const guides = useGuides();
  const homeFaqs = useHomeFaqs();
  const c = useCopy("home");
  const cm = useCopy("common");
  const lp = useLp();
  const fincas = services.filter((s) => s.category === "fincas");
  const edif = services.filter((s) => s.category === "edificaciones");
  return (
    <>
      <SEO
        title={c.seoT}
        description={c.seoD}
        path="/"
        jsonLd={[faqSchema(homeFaqs)]}
      />
      <section className="relative overflow-hidden tech-grid-bg" data-testid="hero-section">
        <ContourLines className="absolute inset-0 w-full h-full" opacity={0.55} />
        <div className="container-site relative pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tech-label mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-terracotta inline-block" aria-hidden="true" />
              {c.kicker}
            </p>
            <h1 data-testid="hero-h1-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-ink">
              <MaskedLine delay={0.1}>{c.h1l1}</MaskedLine>
              <MaskedLine delay={0.25}>{c.h1l2}</MaskedLine>
              <MaskedLine delay={0.4}>
                {c.h1l3a} <span className="text-terracotta">{c.h1l3b}</span>
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.6}>
              <p className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
                {c.intro}
              </p>
            </MaskedLine>
            <Reveal delay={0.75} className="mt-9 flex flex-wrap items-center gap-4">
              <Link to={lp("/contacto/")} data-testid="hero-cta-primary" onClick={() => trackEvent("cta_budget_click", { location: "hero" })} className="btn-primary">
                {cm.tellCase} <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to={lp("/servicios/")} data-testid="hero-cta-secondary" className="btn-secondary">
                {cm.seeServices}
              </Link>
              <a href={business.phoneHref} onClick={() => trackEvent("phone_click", { location: "hero" })}
                 className="flex items-center gap-2 font-mono text-sm text-ink/70 hover:text-terracotta transition-colors" data-testid="hero-phone">
                <Phone size={15} aria-hidden="true" /> {business.phoneDisplay}
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.5} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full border border-cadastral/25 rounded-sm" aria-hidden="true" />
              <img
                src={HERO_IMG}
                alt={c.imgAlt}
                width="1536" height="1024" fetchPriority="high"
                className="relative w-full aspect-[4/3] object-cover rounded-sm"
              />
              <div className="absolute bottom-3 left-3 bg-obsidian/85 backdrop-blur px-3.5 py-2 rounded-sm">
                <p className="font-mono text-[11px] tracking-widest text-slate-300">{c.geo}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-10 sm:py-12" data-testid="brand-band">
        <div className="container-site flex justify-center">
          <Reveal>
            <img
              src="/brand/midefinca-logo-principal-full.png"
              alt="MideFinca: Medimos tu finca. Facilitamos tus trámites."
              width="1857"
              height="386"
              className="w-[310px] sm:w-[390px] h-auto"
              loading="lazy"
              data-testid="brand-logo-home"
            />
          </Reveal>
        </div>
      </section>

      <Marquee />
      <NeedSelector />

      <section className="section-gap !pt-4" data-testid="problems-section">
        <div className="container-site">
          <Reveal>
            <p className="tech-label mb-4">{c.probKicker}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold max-w-2xl leading-snug">
              {c.probTitle}
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.problems.map((p, i) => (
              <Reveal key={p.href} delay={i * 0.04}>
                <Link to={lp(p.href)} data-testid={`home-problem-card-${i}`} className="card-technical group block p-6 h-full">
                  <h3 className="text-base font-semibold text-ink leading-snug group-hover:text-terracotta transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gap bg-paper-tint" data-testid="services-section">
        <div className="container-site">
          <Reveal>
            <p className="tech-label mb-4">{c.servKicker}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">{c.fincasH}</h2>
          </Reveal>
          <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {fincas.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}><ServiceCard service={s} index={i} /></Reveal>
            ))}
          </div>
          <Reveal className="mt-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">{c.edifH}</h2>
          </Reveal>
          <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {edif.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}><ServiceCard service={s} index={fincas.length + i} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gap" data-testid="process-section">
        <div className="container-site">
          <Reveal>
            <p className="tech-label mb-4">{c.procKicker}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold max-w-2xl leading-snug">
              {c.procTitle}
            </h2>
          </Reveal>
          <div className="mt-10">
            <ProcessSteps />
          </div>
        </div>
      </section>

      <TrustSection />

      <section className="section-gap" data-testid="home-conversion-section">
        <div className="container-site">
          <Reveal>
            <div className="border hairline bg-white rounded-sm p-8 sm:p-12 lg:p-16 relative overflow-hidden">
              <ContourLines className="absolute right-0 top-0 w-1/2 h-full pointer-events-none" opacity={0.3} />
              <div className="relative max-w-2xl">
                <p className="tech-label mb-4">{c.convLabel}</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">
                  {c.convTitle}
                </h2>
                <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                  {c.convText}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to={lp("/contacto/")} data-testid="home-conversion-budget"
                        onClick={() => trackEvent("cta_budget_click", { location: "home_conversion" })} className="btn-primary">
                    {cm.quote} <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                  <a href={business.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="home-conversion-whatsapp"
                     onClick={() => trackEvent("whatsapp_click", { location: "home_conversion" })} className="btn-secondary">
                    <MessageCircle size={16} aria-hidden="true" /> {cm.whatsapp}
                  </a>
                </div>
                <p className="mt-5 text-sm text-slate-600">
                  <Link to={lp("/precios/")} className="link-underline" data-testid="home-conversion-prices-link">
                    {cm.priceDepLink}
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-gap" data-testid="guides-section">
        <div className="container-site">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="tech-label mb-4">{c.guidesKicker}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">{c.guidesH}</h2>
            </div>
            <Link to={lp("/guias/")} className="link-underline text-sm font-bold" data-testid="home-guides-link">{cm.allGuides}</Link>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {guides.slice(0, 3).map((g, i) => (
              <Reveal key={g.slug} delay={i * 0.05}><GuideCard guide={g} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gap !pt-0" data-testid="faq-section">
        <div className="container-site max-w-3xl">
          <Reveal>
            <p className="tech-label mb-4">{c.faqKicker}</p>
            <h2 className="text-2xl sm:text-3xl font-semibold leading-snug mb-8">{c.faqH}</h2>
          </Reveal>
          <FAQAccordion faqs={homeFaqs} testid="home-faq" />
        </div>
      </section>

      <CTASection />
    </>
  );
}
